/**
 * Resize Behavior
 *
 * Handles node resizing:
 * - Shows 8 handles (corners + edge midpoints) when single node is selected
 * - Drag handle to resize with opposite corner as anchor
 * - Shift+drag for proportional resize (corners only)
 * - Escape to cancel
 */

import type { ResizeBehaviorContext } from "./types";
import type { Point, ResizeHandlePosition, EditorNode } from "../types";
import {
  formatResizeHandleId,
  parseResizeHandleId,
  getAllRotatedResizeHandlePositions,
} from "../types";
import * as viewState from "../view-state";

/** Default handle radius for hit testing (in screen pixels, scaled by zoom) */
const DEFAULT_HANDLE_RADIUS = 18;

/** Tighter hit radius for the top (north) edge to avoid accidental resize when dragging */
const TOP_EDGE_HANDLE_RADIUS = 6;

/** Edge handles are inset from corners by this amount (matches visual inset) */
const EDGE_HANDLE_INSET_RATIO = 0.1; // 10% from each corner

/**
 * Calculate the shortest distance from a point to a line segment.
 */
function distanceToLineSegment(
  point: Point,
  lineStart: Point,
  lineEnd: Point,
): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    // Line segment is a point
    return Math.sqrt(
      (point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2,
    );
  }

  // Project point onto line, clamped to segment
  let t =
    ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));

  const projX = lineStart.x + t * dx;
  const projY = lineStart.y + t * dy;

  return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
}

/**
 * Hit test for resize handles on a node.
 * Corner handles use point distance, edge handles use line segment distance.
 * Returns the handle ID if point is within handle radius, null otherwise.
 */
function hitTestResizeHandles(
  node: EditorNode,
  point: Point,
  radius: number = DEFAULT_HANDLE_RADIUS,
): string | null {
  // Use rotated handle positions for hit testing
  const handles = getAllRotatedResizeHandlePositions(node);

  // Build a map for quick lookup
  const posMap = new Map(handles.map((h) => [h.position, h.point]));

  // Corner positions for edge handle calculations
  const cornerPositions: ResizeHandlePosition[] = ["nw", "ne", "sw", "se"];
  const edgePositions: ResizeHandlePosition[] = ["n", "s", "e", "w"];

  // Test corner handles first (they have priority at intersections)
  for (const position of cornerPositions) {
    const handlePoint = posMap.get(position)!;
    const dx = point.x - handlePoint.x;
    const dy = point.y - handlePoint.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= radius) {
      return formatResizeHandleId(node.id, position);
    }
  }

  // Test edge handles (full edge minus insets for corners)
  for (const position of edgePositions) {
    let corner1: Point, corner2: Point;
    if (position === "n") {
      corner1 = posMap.get("nw")!;
      corner2 = posMap.get("ne")!;
    } else if (position === "s") {
      corner1 = posMap.get("sw")!;
      corner2 = posMap.get("se")!;
    } else if (position === "w") {
      corner1 = posMap.get("nw")!;
      corner2 = posMap.get("sw")!;
    } else {
      // 'e'
      corner1 = posMap.get("ne")!;
      corner2 = posMap.get("se")!;
    }

    // Calculate edge length
    const edgeDx = corner2.x - corner1.x;
    const edgeDy = corner2.y - corner1.y;
    const edgeLength = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);

    // Apply inset proportionally (same as visual handles)
    const inset = Math.min(radius, edgeLength * EDGE_HANDLE_INSET_RATIO);
    const insetRatio = inset / edgeLength;

    // Calculate inset endpoints
    const lineStart: Point = {
      x: corner1.x + edgeDx * insetRatio,
      y: corner1.y + edgeDy * insetRatio,
    };
    const lineEnd: Point = {
      x: corner2.x - edgeDx * insetRatio,
      y: corner2.y - edgeDy * insetRatio,
    };

    const distance = distanceToLineSegment(point, lineStart, lineEnd);

    // Use a tighter hit zone on the top edge so users don't accidentally
    // trigger resize when they intend to drag from the top of the node.
    const edgeRadius = position === "n" ? radius * (TOP_EDGE_HANDLE_RADIUS / DEFAULT_HANDLE_RADIUS) : radius;

    if (distance <= edgeRadius) {
      return formatResizeHandleId(node.id, position);
    }
  }

  return null;
}

/**
 * Register resize behavior handlers.
 * Returns cleanup function to unregister all handlers.
 */
export function registerResizeBehavior(ctx: ResizeBehaviorContext): () => void {
  const {
    input,
    getViewState,
    updateViewState,
    getNodes,
    screenToWorld,
    resizeNode,
    minNodeSize = { width: 50, height: 50 },
    handleRadius = DEFAULT_HANDLE_RADIUS,
    shouldLockAspectRatio,
    getZoom,
    getActualNodeSize,
    getGridSize,
    isResizable,
    getIntrinsicAspectRatio,
    log,
  } = ctx;
  const unsubscribers: (() => void)[] = [];

  /**
   * Get the effective handle radius in world units.
   * Scales by 1/zoom so the hit area matches the visual handle size.
   */
  function getEffectiveRadius(): number {
    const zoom = getZoom?.() ?? 1;
    // Visual handles are handleRadius screen pixels, so hit area should be handleRadius/zoom world units
    return handleRadius / zoom;
  }

  /**
   * Get node with actual rendered size.
   */
  function getNodeWithActualSize(nodeId: string): EditorNode | undefined {
    const node = getNodes().find((n) => n.id === nodeId);
    if (!node) return undefined;

    // If we have a callback to get actual size, use it
    if (getActualNodeSize) {
      const actualSize = getActualNodeSize(nodeId);
      if (actualSize) {
        return {
          ...node,
          size: actualSize,
        };
      }
    }

    return node;
  }

  // =========================================================================
  // Handle Hover Detection
  // =========================================================================

  unsubscribers.push(
    input.on("mousemove", { mode: "*" }, (inputCtx) => {
      const state = getViewState();
      const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);

      // Only show resize handles when exactly one node is selected
      if (state.selection.size !== 1) {
        if (state.hovered.resizeHandleId) {
          updateViewState(viewState.setHoveredResizeHandle(state, null));
        }
        return;
      }

      // Don't update hover during other operations (except resizing)
      if (state.drag || state.connecting || state.boxSelect) {
        return;
      }

      // Skip if currently resizing (hover stays on active handle)
      if (state.resize) {
        return;
      }

      const selectedNodeId = [...state.selection][0];

      // Check if node is resizable
      if (isResizable && !isResizable(selectedNodeId)) {
        if (state.hovered.resizeHandleId) {
          updateViewState(viewState.setHoveredResizeHandle(state, null));
        }
        return;
      }

      const node = getNodeWithActualSize(selectedNodeId);
      if (!node) {
        if (state.hovered.resizeHandleId) {
          updateViewState(viewState.setHoveredResizeHandle(state, null));
        }
        return;
      }

      // Hit test resize handles on the selected node
      // Use zoom-scaled radius so hit area matches visual handle size
      const effectiveRadius = getEffectiveRadius();
      const handleId = hitTestResizeHandles(node, point, effectiveRadius);

      if (handleId !== state.hovered.resizeHandleId) {
        updateViewState(viewState.setHoveredResizeHandle(state, handleId));
      }
    }),
  );

  // =========================================================================
  // Start Resize (mousedown on resize handle)
  // =========================================================================

  unsubscribers.push(
    input.on("mousedown", { mode: "default" }, (inputCtx) => {
      // Ignore middle mouse button
      if (inputCtx.button === 1) return;

      const state = getViewState();

      // Only start resize if hovering a resize handle
      const hoveredHandle = state.hovered.resizeHandleId;
      if (!hoveredHandle) return;

      const parsed = parseResizeHandleId(hoveredHandle);
      if (!parsed) return;

      const node = getNodeWithActualSize(parsed.nodeId);
      if (!node) return;

      const bounds = {
        x: node.position.x,
        y: node.position.y,
        width: node.size.width,
        height: node.size.height,
      };

      // Get rotation (default to 0 if not set)
      const rotation = node.rotation ?? 0;

      // Start resize
      updateViewState(
        viewState.startResize(
          state,
          parsed.nodeId,
          parsed.position,
          bounds,
          rotation,
        ),
      );
      input.pushMode("resize");
      log?.("resize-start", `${parsed.nodeId} handle:${parsed.position}`);
      inputCtx.stopPropagation();
    }),
  );

  // =========================================================================
  // Resize Move
  // =========================================================================

  unsubscribers.push(
    input.on("mousemove", { mode: "resize" }, (inputCtx) => {
      const state = getViewState();
      const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);

      // Determine if aspect ratio should be locked
      // For nodes with shouldLockAspectRatio = true, Shift UNLOCKS (inverted)
      // For other nodes, Shift LOCKS (default)
      let proportional = inputCtx.modifiers.shift;
      const nodeId = state.resize?.nodeId;
      if (nodeId && shouldLockAspectRatio?.(nodeId)) {
        proportional = !inputCtx.modifiers.shift;
      }

      const gridSize = getGridSize?.() ?? 0;
      // Get intrinsic aspect ratio for images (uses actual image dimensions, not node size)
      const intrinsicAspectRatio = nodeId
        ? getIntrinsicAspectRatio?.(nodeId)
        : undefined;
      updateViewState(
        viewState.updateResize(
          state,
          point,
          minNodeSize,
          proportional,
          gridSize,
          intrinsicAspectRatio,
        ),
      );
    }),
  );

  // =========================================================================
  // Resize End
  // =========================================================================

  unsubscribers.push(
    input.on("mouseup", { mode: "resize" }, (inputCtx) => {
      const state = getViewState();

      if (state.resize) {
        const { nodeId, currentBounds } = state.resize;
        // Apply the resize
        resizeNode(nodeId, currentBounds);
        log?.(
          "resize-end",
          `${nodeId} -> (${currentBounds.width.toFixed(0)}x${currentBounds.height.toFixed(0)})`,
        );
      }

      // Finish resize state
      updateViewState(viewState.finishResize(getViewState()));
      input.popMode("resize");
      // Prevent event from reaching other behaviors (e.g., selection starting a box-select)
      inputCtx.stopPropagation();
    }),
  );

  // =========================================================================
  // Cancel Resize
  // =========================================================================

  unsubscribers.push(
    input.on(
      "keydown",
      "escape",
      {
        mode: "resize",
        command: { id: "resize.cancel", name: "Cancel Resize" },
      },
      () => {
        log?.("resize-cancel", "");
        updateViewState(viewState.cancelResize(getViewState()));
        input.popMode("resize");
      },
    ),
  );

  // =========================================================================
  // Cleanup
  // =========================================================================

  return () => {
    for (const unsub of unsubscribers) {
      unsub();
    }
  };
}
